import {
  addDoc,
  collection,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "@/api/firebase/firebase";
import {
  ChatAttachment,
  ChatMessage,
  ChatThread,
  SendMessageInput,
} from "@/types/chat";

const THREADS = "chatThreads";
const MESSAGES = "messages";
const STORAGE_PATH = "chat-attachments";
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

export const normalizeEmail = (email: string) =>
  (email || "").trim().toLowerCase();

// Thread ids are deterministic so the trainer and the participant both resolve
// the same document without a lookup. Emails can't contain "/", so they are
// safe inside a document id.
export const buildThreadId = (cohortId: string, participantEmail: string) =>
  `${cohortId}__${normalizeEmail(participantEmail)}`;

const toDate = (value: any): Date | null =>
  value?.toDate ? value.toDate() : value instanceof Date ? value : null;

const mapThread = (id: string, data: any): ChatThread => ({
  id,
  cohortId: data?.cohortId || "",
  cohortName: data?.cohortName || "",
  participantEmail: data?.participantEmail || "",
  participantName: data?.participantName || "",
  lastMessage: data?.lastMessage || "",
  lastMessageAt: toDate(data?.lastMessageAt),
  lastSenderRole: data?.lastSenderRole || null,
  unreadForParticipant: data?.unreadForParticipant || 0,
  unreadForStaff: data?.unreadForStaff || 0,
});

const mapMessage = (id: string, data: any): ChatMessage => ({
  id,
  text: data?.text || "",
  senderId: data?.senderId || "",
  senderName: data?.senderName || "",
  senderRole: data?.senderRole === "staff" ? "staff" : "participant",
  attachments: Array.isArray(data?.attachments) ? data.attachments : [],
  createdAt: toDate(data?.createdAt),
});

export const ChatService = {
  buildThreadId,

  // Live updates for one thread (used for the unread badge and the header).
  subscribeToThread(
    threadId: string,
    onChange: (thread: ChatThread | null) => void,
  ) {
    return onSnapshot(
      doc(db, THREADS, threadId),
      (snap) => onChange(snap.exists() ? mapThread(snap.id, snap.data()) : null),
      (error) => {
        console.error("[chat] thread subscription failed", error);
        onChange(null);
      },
    );
  },

  // Every thread in a cohort — the staff-side list and per-participant badges.
  subscribeToCohortThreads(
    cohortId: string,
    onChange: (threads: ChatThread[]) => void,
  ) {
    return onSnapshot(
      query(collection(db, THREADS), where("cohortId", "==", cohortId)),
      (snap) => onChange(snap.docs.map((d) => mapThread(d.id, d.data()))),
      (error) => {
        console.error("[chat] cohort threads subscription failed", error);
        onChange([]);
      },
    );
  },

  subscribeToMessages(
    threadId: string,
    onChange: (messages: ChatMessage[]) => void,
  ) {
    return onSnapshot(
      query(
        collection(db, THREADS, threadId, MESSAGES),
        orderBy("createdAt", "asc"),
      ),
      (snap) => onChange(snap.docs.map((d) => mapMessage(d.id, d.data()))),
      (error) => {
        console.error("[chat] message subscription failed", error);
        onChange([]);
      },
    );
  },

  async uploadAttachment(
    threadId: string,
    file: File,
  ): Promise<ChatAttachment> {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      throw new Error("Files must be 10MB or smaller.");
    }
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "_");
    const path = `${STORAGE_PATH}/${threadId}/${Date.now()}_${safeName}`;
    const snapshot = await uploadBytes(ref(storage, path), file);
    const url = await getDownloadURL(snapshot.ref);
    return {
      name: file.name,
      url,
      size: file.size,
      contentType: file.type || "application/octet-stream",
    };
  },

  // Writes the message and bumps the *other* side's unread counter, creating
  // the thread on first use.
  async sendMessage(input: SendMessageInput): Promise<void> {
    const {
      cohortId,
      cohortName,
      participantEmail,
      participantName,
      text,
      attachments = [],
      senderId,
      senderName,
      senderRole,
    } = input;

    const body = (text || "").trim();
    if (!body && attachments.length === 0) return;

    const threadId = buildThreadId(cohortId, participantEmail);
    const threadRef = doc(db, THREADS, threadId);

    const preview =
      body ||
      (attachments.length === 1
        ? attachments[0].name
        : `${attachments.length} attachments`);

    // The thread is written first: access to the messages subcollection is
    // granted by looking at the parent thread, so it has to exist before the
    // participant's very first message.
    await setDoc(
      threadRef,
      {
        cohortId,
        cohortName: cohortName || "",
        participantEmail: normalizeEmail(participantEmail),
        participantName: participantName || "",
        lastMessage: preview,
        lastMessageAt: serverTimestamp(),
        lastSenderRole: senderRole,
        updatedAt: serverTimestamp(),
        ...(senderRole === "staff"
          ? { unreadForParticipant: increment(1) }
          : { unreadForStaff: increment(1) }),
      },
      { merge: true },
    );

    await addDoc(collection(threadRef, MESSAGES), {
      text: body,
      senderId,
      senderName,
      senderRole,
      attachments,
      createdAt: serverTimestamp(),
    });
  },

  // Sends one message (text and/or files) from staff to every listed
  // participant, into each participant's own thread — so it arrives exactly
  // like a normal 1:1 message. Files are uploaded once and the same
  // attachment URLs are reused across all the messages.
  async sendBroadcast(input: {
    cohortId: string;
    cohortName?: string;
    recipients: { email: string; name?: string }[];
    text: string;
    files?: File[];
    senderId: string;
    senderName: string;
    onProgress?: (sent: number, total: number) => void;
  }): Promise<{ sent: number; failed: string[] }> {
    const {
      cohortId,
      cohortName,
      recipients,
      text,
      files = [],
      senderId,
      senderName,
      onProgress,
    } = input;

    const body = (text || "").trim();
    if ((!body && files.length === 0) || recipients.length === 0) {
      return { sent: 0, failed: [] };
    }

    const attachments: ChatAttachment[] = [];
    for (const file of files) {
      attachments.push(
        await ChatService.uploadAttachment(`broadcast__${cohortId}`, file),
      );
    }

    const failed: string[] = [];
    let sent = 0;
    for (const recipient of recipients) {
      try {
        await ChatService.sendMessage({
          cohortId,
          cohortName,
          participantEmail: recipient.email,
          participantName: recipient.name || "",
          text: body,
          attachments,
          senderId,
          senderName,
          senderRole: "staff",
        });
        sent += 1;
      } catch (error) {
        console.error("[chat] broadcast to", recipient.email, "failed", error);
        failed.push(recipient.name || recipient.email);
      }
      onProgress?.(sent, recipients.length);
    }
    return { sent, failed };
  },

  // Clears the reader's unread counter. Uses update (not merge-create) so a
  // thread is never created without its identifying fields.
  async markRead(
    threadId: string,
    reader: "staff" | "participant",
  ): Promise<void> {
    try {
      await updateDoc(
        doc(db, THREADS, threadId),
        reader === "staff"
          ? { unreadForStaff: 0 }
          : { unreadForParticipant: 0 },
      );
    } catch (error) {
      // Thread doesn't exist yet — nothing to clear.
      if ((error as any)?.code !== "not-found") {
        console.error("[chat] failed to mark thread read", error);
      }
    }
  },
};

export default ChatService;
